<template>
  <div
    class="tw-relative tw-p-5 tw-rounded-lg tw-shadow-sm tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-200"
  >
    <div class="tw-flex tw-gap-3">
      <Title
        tag="h3"
        size="5"
        class="tw-grow tw-mb-2"
      >
        {{ props.note.title }}
      </Title>

      <p class="tw-text-sm tw-text-gray-500">
        {{ displayDate }}
      </p>
    </div>

    <div
      class="content"
      v-html="renderMarkdown(props.note.content)"
    />

    <div class="tw-absolute tw-right-0 tw-bottom-0">
      <IconButton
        class="tw-p-5 tw-pr-3"
        icon="fa fa-edit"
        title="Edit"
        @click="editMode = true"
      >
        Edit
      </IconButton>

      <IconButton
        class="tw-p-5 tw-pl-3"
        icon="fa fa-trash"
        title="Delete"
        @click="confirmDelete = true"
      >
        Delete
      </IconButton>
    </div>

    <StakeModal v-model="editMode">
      <CreateEditNote
        :entity-external-id="entityExternalId"
        :note="props.note"
        :loading="loading"
        @refresh="handleRefresh"
        @close="editMode = false"
      />
    </StakeModal>

    <StakeConfirm
      v-model="confirmDelete"
      title="Delete Note"
      message="Are you sure you want to delete this note? This cannot be undone."
      button-text="Delete"
      :loading="loading"
      @confirm="deleteNote()"
      @cancel="confirmDelete = false"
    />
  </div>
</template>


<script setup>
import { ref, computed, defineProps } from 'vue';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { IconButton, Title } from '@stakeui';
import StakeModal from '@stakeui/StakeModal.vue';
import StakeConfirm from '@stakeui/StakeConfirm.vue';
import CreateEditNote from '@features/notes/components/CreateEditNote.vue';


import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
const advisorStore = useAdvisorStore();

// Configure marked
marked.setOptions({
  headerIds: false,
  mangle: false,
  breaks: true,
});


const editMode = ref(false);
const confirmDelete = ref(false);
const loading = ref(false);


const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
  note: {
    type: Object,
    required: true,
  },
});


const displayDate = computed(() => {
  const date = new Date(props.note.updated_at);
  return date.toLocaleString();
});

const renderMarkdown = (text) => {
  if (!text) return '';
  // First convert markdown to HTML, then sanitize the output
  const htmlContent = marked(text);
  return DOMPurify.sanitize(htmlContent);
};


const emit = defineEmits(['refresh', 'close']);


const deleteNote = async () => {
  if (loading.value) return;
  loading.value = true;

  try {
    await fetchWrapper(`/api/v1/notes/${props.note.external_id}/`, {
      method: 'DELETE',
      headers: advisorStore.headers,
    });

    emit('refresh');
  } catch (err) {
    console.error('Failed to delete note:', err);
  } finally {
    loading.value = false;
  }

  emit('refresh');
};


const handleRefresh = () => {
  emit('refresh');
  editMode.value = false;
};

</script>
